/**
 * Tax Injector Upload Response Types
 *
 * The tax injector upload endpoint can return different response types:
 * - A successful upload object with ExternalId for async processing
 * - An error object with Code and Message
 * - A validation error object with CSV row errors
 */

/**
 * Field error within a CSV row error
 */
export interface CsvFieldError {
  Field: string
  Message: string
  Code?: string
}

/**
 * CSV row error containing field errors
 */
export interface CsvRowError {
  RowNumber: number
  FieldErrors: CsvFieldError[]
}

/**
 * Validation error response from tax injector upload
 */
export interface TaxInjectorValidationError {
  Code: 'VALIDATION_ERROR'
  Message: string
  CsvRowErrors: CsvRowError[]
}

/**
 * Generic error response from tax injector upload
 */
export interface TaxInjectorError {
  Code: string
  Message: string
}

/**
 * Successful upload response from tax injector
 */
export interface TaxInjectorUploadSuccess {
  ExternalId: string
  CreatedAt: string
  CreatedBy: string
}

/**
 * Union type for tax injector upload response
 * Can be:
 * - A successful upload object with ExternalId
 * - A validation error object
 * - A generic error object
 */
export type TaxInjectorUploadResponse =
  | TaxInjectorUploadSuccess
  | TaxInjectorValidationError
  | TaxInjectorError

/**
 * Tax injector summary from GET /v1/tax-engine/toolbox/injector
 */
export interface TaxInjectorSummary {
  ExternalId: string
  CreatedAt: string
  CreatedBy: string
  Filename?: string | null
}

/**
 * Paginated response for tax injector list
 */
export interface TaxInjectorListResponse {
  Content: TaxInjectorSummary[]
  TotalPages: number
  TotalElements: number
  Size: number
  Number: number
  NumberOfElements: number
  First: boolean
  Last: boolean
  Empty: boolean
  Sort: string[]
}

/**
 * Possible status values for tax injector records.
 * - PENDING: awaiting Kafka consumer processing
 * - COMPLETED: tax transaction created successfully
 * - ALREADY_PROCESSED: idempotency hit — record already had a transaction
 * - SKIPPED: business-rule skip (TaxConfig start date after tax date, or invalid shipping country)
 * - ERROR: DLT handler — message processing failed
 */
export type TaxInjectorRecordStatus =
  | 'PENDING'
  | 'COMPLETED'
  | 'ALREADY_PROCESSED'
  | 'SKIPPED'
  | 'ERROR'

/**
 * Tax injector record from GET /v1/tax-engine/toolbox/injector/:externalId/records
 */
export interface TaxInjectorRecord {
  Id: number
  Reference: string
  Status: TaxInjectorRecordStatus
  TransactionExternalId?: string | null
  MerchantId?: string | null
  CreatedAt: string
}

/**
 * Paginated response for tax injector records
 */
export interface TaxInjectorRecordsResponse {
  Content: TaxInjectorRecord[]
  TotalPages: number
  TotalElements: number
  Size: number
  Number: number
  NumberOfElements: number
  First: boolean
  Last: boolean
  Empty: boolean
  Sort: string[]
}

/**
 * Type guard to check if response is a successful upload
 */
export function isUploadSuccess(
  response: TaxInjectorUploadResponse,
): response is TaxInjectorUploadSuccess {
  return (
    typeof response === 'object' &&
    'ExternalId' in response &&
    !('Code' in response)
  )
}

/**
 * Type guard to check if response is a validation error
 */
export function isValidationError(
  response: TaxInjectorUploadResponse,
): response is TaxInjectorValidationError {
  return (
    typeof response === 'object' &&
    'Code' in response &&
    response.Code === 'VALIDATION_ERROR'
  )
}

/**
 * Download URL resource response from tax injector download endpoint
 */
export interface DownloadUrlResource {
  downloadUrl: string
  expiresInSeconds: number
}

